import React, { useState, useEffect } from "react";
import { Target, Home, Key, Clock, Loader2, MapPin, Megaphone, Flame } from "lucide-react";
import { getProspecting, generateAreaCampaign } from "../../api";
import { useToast } from "../../hooks/use-toast";

const gbp = (n) => `\u00A3${Number(n || 0).toLocaleString()}`;

const TEMP = {
  fresh: { label: "Fresh", cls: "bg-green-100 text-green-700" },
  watch: { label: "Watch", cls: "bg-amber-100 text-amber-700" },
  stale: { label: "Stale", cls: "bg-red-100 text-red-600" },
};

const ProspectCard = ({ p, onCanvass, busyId }) => {
  const t = TEMP[p.temperature] || TEMP.fresh;
  return (
    <div className="bg-white rounded-2xl border border-[#f0ece5] overflow-hidden flex flex-col" data-testid="prospect-listing-card">
      <div className="relative h-32">
        {p.image ? <img src={p.image} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full bg-[#faf7f2]" />}
        <span className={`absolute top-2 right-2 text-[11px] px-2 py-0.5 rounded-full font-500 ${t.cls}`}>{t.label}</span>
        <span className="absolute bottom-2 left-2 text-[11px] px-2 py-0.5 rounded-full bg-black/70 text-white flex items-center gap-1"><Clock size={11} /> {p.daysOnMarket} days on market</span>
      </div>
      <div className="p-4 flex-1 flex flex-col">
        <p className="text-[14px] font-500 text-[#2b2b2b] truncate">{p.title}</p>
        <p className="text-[12px] text-[#8a8a8a] truncate flex items-center gap-1"><MapPin size={11} className="text-[#c9b48f]" /> {p.address || p.postcode}</p>
        <p className="text-[13px] font-500 text-[#b8935f] mt-1">{gbp(p.price)}</p>
        <p className="text-[12px] text-[#6a6a6a] mt-2 leading-relaxed">{p.angle}</p>
        <div className="mt-2.5 rounded-lg bg-[#faf7f2] border border-[#f0ece5] p-2.5">
          <p className="text-[11px] text-[#a0a0a0] uppercase tracking-wide mb-0.5">Next action</p>
          <p className="text-[12px] text-[#3a3a3a]">{p.action}</p>
        </div>
        <button onClick={() => onCanvass(p)} disabled={busyId === p.id} data-testid="prospect-canvass-btn"
          className="mt-3 inline-flex items-center justify-center gap-1.5 h-9 rounded-lg bg-[#1c1c1e] text-white text-[12px] font-500 hover:bg-black transition-colors disabled:opacity-60">
          {busyId === p.id ? <Loader2 size={13} className="animate-spin" /> : <Megaphone size={13} />} Draft canvassing campaign
        </button>
      </div>
    </div>
  );
};

const Section = ({ title, icon: Icon, summary, items, onCanvass, busyId, empty }) => (
  <div className="mb-8">
    <div className="flex items-center justify-between mb-4">
      <h3 className="font-display text-[19px] font-500 text-[#2b2b2b] flex items-center gap-2"><Icon size={18} className="text-[#b8935f]" /> {title}</h3>
      {summary && (
        <div className="flex items-center gap-2 text-[12px]">
          <span className="px-2.5 py-1 rounded-full bg-white border border-[#f0ece5] text-[#6a6a6a]">{summary.count} active</span>
          <span className="px-2.5 py-1 rounded-full bg-white border border-[#f0ece5] text-[#6a6a6a]">avg {summary.avgDays}d</span>
          {summary.stale > 0 && <span className="px-2.5 py-1 rounded-full bg-red-50 text-red-600 flex items-center gap-1"><Flame size={12} /> {summary.stale} stale</span>}
        </div>
      )}
    </div>
    {items.length === 0
      ? <p className="text-[13px] text-[#8a8a8a] bg-white rounded-xl border border-[#f0ece5] p-6 text-center">{empty}</p>
      : <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">{items.map((p) => <ProspectCard key={p.id} p={p} onCanvass={onCanvass} busyId={busyId} />)}</div>}
  </div>
);

const GrowthProspecting = () => {
  const { toast } = useToast();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(true);
  const [busyId, setBusyId] = useState(null);

  useEffect(() => { getProspecting().then(setData).catch(() => setData(null)).finally(() => setBusy(false)); }, []);

  const canvass = async (p) => {
    setBusyId(p.id);
    const area = (p.address || "").split(",")[1]?.trim() || p.postcode;
    try {
      await generateAreaCampaign(area || p.postcode, p.postcode);
      toast({ title: "Canvassing campaign drafted", description: `AI content for ${area || p.postcode} saved to Marketing → Pending Review.` });
    } catch { toast({ title: "Couldn't draft campaign", description: "Please try again." }); }
    finally { setBusyId(null); }
  };

  if (busy) return <div className="flex items-center justify-center py-24 text-[#8a8a8a]"><Loader2 className="animate-spin mr-2" size={18} /> Loading prospecting…</div>;
  if (!data) return <div className="bg-white rounded-2xl border border-[#f0ece5] p-8 text-center text-[#8a8a8a]">Couldn't load prospecting data.</div>;

  return (
    <div data-testid="growth-prospecting">
      <div className="bg-white rounded-2xl border border-[#f0ece5] p-5 mb-6 flex items-start gap-3">
        <Target size={20} className="text-[#b8935f] mt-0.5 shrink-0" />
        <p className="text-[13px] text-[#6a6a6a] leading-relaxed">Turn your live stock into pipeline. Each active listing anchors local prospecting — sellers around your for-sale boards, landlords around your lettings — with days-on-market flagging where to focus.</p>
      </div>
      <Section title="Seller prospecting" icon={Home} summary={data.summary?.sellers} items={data.sellers || []}
        onCanvass={canvass} busyId={busyId} empty="No active sales listings to prospect around yet." />
      <Section title="Landlord prospecting" icon={Key} summary={data.summary?.landlords} items={data.landlords || []}
        onCanvass={canvass} busyId={busyId} empty="No active lettings listings to prospect around yet." />
    </div>
  );
};

export default GrowthProspecting;
